import type {
  ParsedFileData,
  DocumentTypeConfig,
  ExtractionError,
  ExtractionWarning,
} from "./types";
import { findSheetByNameOrIndex, getCellValue } from "./parsers";

type VersionValidationResult = {
  version: string | null;
  isValid: boolean;
  error: ExtractionError | null;
  warning: ExtractionWarning | null;
};

export function validateVersion(
  parsedFile: ParsedFileData,
  config: DocumentTypeConfig
): VersionValidationResult {
  const formatConfig =
    parsedFile.fileFormat === "xlsx" ? config.xlsx : config.pdf;

  if (!formatConfig?.version) {
    return { version: null, isValid: true, error: null, warning: null };
  }

  const versionConfig = formatConfig.version;
  const sheet = findSheetByNameOrIndex(
    parsedFile,
    versionConfig.sheetName,
    versionConfig.sheetIndex
  );

  if (!sheet) {
    return {
      version: null,
      isValid: false,
      error: {
        file: parsedFile.fileName,
        code: "SHEET_NOT_FOUND",
        message: `Version sheet "${versionConfig.sheetName ?? `index ${versionConfig.sheetIndex}`}" not found`,
      },
      warning: null,
    };
  }

  const rawValue = getCellValue(sheet, versionConfig.cell);
  const actualVersion = rawValue ? String(rawValue).trim() : null;

  if (!actualVersion) {
    return {
      version: null,
      isValid: false,
      error: {
        file: parsedFile.fileName,
        code: "VERSION_MISMATCH",
        message: `Version cell ${versionConfig.cell} is empty`,
      },
      warning: null,
    };
  }

  const isValid = versionConfig.expectedVersions.some(
    (expected) =>
      actualVersion === expected ||
      actualVersion.startsWith(expected + "_") ||
      actualVersion.startsWith(expected + " ")
  );

  if (!isValid) {
    return {
      version: actualVersion,
      isValid: false,
      error: {
        file: parsedFile.fileName,
        code: "VERSION_MISMATCH",
        message: `Unsupported version "${actualVersion}". Expected one of: ${versionConfig.expectedVersions.join(", ")}`,
      },
      warning: null,
    };
  }

  return { version: actualVersion, isValid: true, error: null, warning: null };
}
